import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import { GetFile } from "common/general";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"];

export default function FileViewer() {
	const params = useParams();
	const filePath = params["*"];

	const extension = filePath?.split(".").pop()?.toLowerCase();
	const fileName = filePath?.split("/").pop();

	const [base64Data, setBase64Data] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const blobUrl = useMemo(() => {
		if (!base64Data) return null;
		const isPdf = extension === "pdf";
		const isImage = IMAGE_EXTENSIONS.includes(extension);
		if (!isPdf && !isImage) return null;

		const byteCharacters = atob(base64Data);
		const byteArray = new Uint8Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteArray[i] = byteCharacters.charCodeAt(i);
		}
		const mimeType = isPdf ? "application/pdf" : `image/${extension}`;
		const blob = new Blob([byteArray], { type: mimeType });
		return URL.createObjectURL(blob);
	}, [base64Data, extension]);

	useEffect(() => {
		return () => {
			if (blobUrl) URL.revokeObjectURL(blobUrl);
		};
	}, [blobUrl]);

	useEffect(() => {
		if (!filePath) {
			setError("Invalid file path.");
			setLoading(false);
			return;
		}

		const fetchData = async () => {
			try {
				var response = await GetFile(filePath.replace(/\//g, ","));

				if (response?.message) {
					setBase64Data(response.message);
				} else {
					setError("No file data returned.");
				}
			} catch (error) {
				console.error("Error fetching file:", error);
				setError("Failed to load file.");
			}
			setLoading(false);
		};
		fetchData();
	}, [filePath]);

	if (loading) {
		return (
			<div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
				<Spinner animation="border" role="status">
					<span className="visually-hidden">Loading...</span>
				</Spinner>
			</div>
		);
	}

	if (error) {
		return (
			<div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
				<Alert variant="danger">{error}</Alert>
			</div>
		);
	}

	const isPdf = extension === "pdf";
	const isImage = IMAGE_EXTENSIONS.includes(extension);

	if (isPdf && blobUrl) {
		return (
			<div style={{ width: "100%", height: "85vh" }}>
				<iframe src={blobUrl} title="PDF Viewer" width="100%" height="100%" style={{ border: "none" }} />
			</div>
		);
	}

	if (isImage && blobUrl) {
		return (
			<div className="d-flex justify-content-center align-items-center p-3">
				<a href={blobUrl} download={fileName}>
					<img src={blobUrl} alt="File preview" style={{ maxWidth: "100%", maxHeight: "85vh", objectFit: "contain", cursor: "pointer" }} />
				</a>
			</div>
		);
	}

	return (
		<div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
			<Alert variant="warning">Unsupported file type: {extension}</Alert>
		</div>
	);
}
